/**
 * Pure items-mutation used by KanbanBoard during onDragOver.
 *
 * Removes `activeId` from `sourceCol`, inserts before `overId` in `destCol`.
 * If `overId` equals the destination column name (empty-column drop), appends
 * to the end. If the move is intra-column AND the source index is before the
 * target index, adjusts by 1 so the result reflects "drop before the target".
 *
 * Returns the same reference when activeId isn't found — lets React bail on
 * a no-op state update.
 */
type WithIdSortStatus = { id: string; sortOrder: number; status: string };

export function moveBetween<
  T extends WithIdSortStatus,
  M extends Record<string, T[]>,
>(
  items: M,
  activeId: string,
  sourceCol: string,
  destCol: string,
  overId: string,
): M {
  const sourceList = items[sourceCol];
  if (!sourceList) return items;
  const sourceIdx = sourceList.findIndex((t) => t.id === activeId);
  if (sourceIdx === -1) return items;

  const isSameCol = sourceCol === destCol;
  const destOriginal = items[destCol] ?? [];

  // Compute destIdx against the ORIGINAL destination list (before any
  // mutation), so the index reflects the user's intent against what they see.
  // overId === column name means "drop into empty/end of column".
  let destIdx =
    overId === destCol
      ? destOriginal.length
      : destOriginal.findIndex((t) => t.id === overId);
  if (destIdx === -1) destIdx = destOriginal.length;

  const nextSource = [...sourceList];
  const [moving] = nextSource.splice(sourceIdx, 1);
  if (!moving) return items;

  const destList = isSameCol ? nextSource : [...destOriginal];

  // Same-column adjustment: removing the item before its target shifts the
  // target up by 1.
  const adjustedDestIdx =
    isSameCol && sourceIdx < destIdx ? destIdx - 1 : destIdx;
  destList.splice(adjustedDestIdx, 0, moving);

  if (isSameCol) return { ...items, [sourceCol]: destList };
  return { ...items, [sourceCol]: nextSource, [destCol]: destList };
}
